import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  UserGear, UsersThree, Megaphone, ClockCounterClockwise, Warning, Columns, LockKey, Handshake,
} from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { Frame } from '../../components/shell/AppShell';
import PageHeader from '../../components/shell/PageHeader';
import SettingRow, { SettingGroup } from '../../components/shell/SettingRow';
import AccountSections from '../../components/more/AccountSections';
import { navFor } from '../../lib/navigation';
import adminArt from '../../assets/bg/founder-admin.webp';

const ICONS = {
  operators: UserGear,
  assignments: UsersThree,
  broadcast: Megaphone,
  audit: ClockCounterClockwise,
  'last-resort': Warning,
  commons: Columns,
  vault: LockKey,
  sworn: Handshake,
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HINTS = {
  'last-resort': 'Destroys everything. Guarded on the action, not hidden.',
};

/**
 * More, for staff. The administrative groups are whatever navFor() gives this
 * role -- an operator's More is built from an operator's capabilities, not a
 * Founder's page with cards hidden -- followed by the same personal settings
 * members have. The Society group keeps both roles' paths to the Commons and
 * their own Vault, and to the Sworn only for those in it.
 */
const StaffMore = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const groups = navFor(user).more.filter((g) => g.items.length > 0);

  // The circle has to start with somebody, and it is the Founder's circle:
  // he steps in first, through the same admission the server already has
  // (POST /api/sworn/members/:id). Until he does, the invitation's third
  // sentence -- "The Founder is in the room" -- would not be true, and the
  // first person to accept would arrive in an empty room. Shown only to the
  // Founder and only while he is outside; afterwards The Sworn takes its place.
  const canFound = user?.role === 'superadmin' && user?.is_sworn !== true;
  const [founding, setFounding] = useState(false);
  const found = async () => {
    if (founding) return;
    if (!window.confirm(
      'Enter the Sworn?\n\n'
      + 'You become the first member of the circle. Everyone you invite and who '
      + 'accepts will see your name there, as you will see theirs.',
    )) return;
    setFounding(true);
    try {
      await axios.post(`${API_URL}/api/sworn/members/${user.id}`, {}, { withCredentials: true });
    } catch (e) {
      // 400 means already in the circle -- the outcome wanted either way.
      if (e.response?.status !== 400) {
        toast.error('That did not go through. Try again.');
        setFounding(false);
        return;
      }
    }
    // is_sworn reaches the app through /auth/me, so re-read it before the
    // room exists for him.
    await checkAuth();
    navigate('/sworn');
  };

  return (
    <Frame user={user} background={adminArt} scrim={0.55} activeKey="more">
      <div data-testid="staff-more">
        <PageHeader title="More" subtitle={user?.role === 'superadmin' ? 'Operations · Governance · Society' : 'Governance · Society'} />
        <div className="max-w-lg mx-auto px-4 pb-6 pt-2 space-y-5">
          {groups.map((g) => (
            <SettingGroup key={g.key} title={g.label} testid={`more-${g.key}`}>
              {g.items.map((i) => (
                <SettingRow
                  key={i.key}
                  icon={ICONS[i.key]}
                  label={i.label}
                  hint={HINTS[i.key]}
                  tone={i.key === 'last-resort' ? 'destructive' : undefined}
                  onClick={() => navigate(i.to)}
                  data-testid={`more-${i.key}`}
                />
              ))}
              {g.key === 'society' && canFound && (
                <SettingRow
                  icon={Handshake}
                  label="Enter the Sworn"
                  hint="Step into your circle first, before anyone you invite accepts."
                  onClick={found}
                  disabled={founding}
                  data-testid="more-enter-sworn"
                />
              )}
            </SettingGroup>
          ))}
          <AccountSections staff />
        </div>
      </div>
    </Frame>
  );
};

export default StaffMore;
